import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import classNames from 'classnames'
import moment from 'moment'
import { nanoid } from 'nanoid'

import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc'

import Button from './button'
import ButtonTabs from './button-tabs'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import HeaderPanel, { Panel } from './header-panel'
import Input, { Label } from './input'
import Modal from './modal'
import Row, { LabelSlot } from './row'
import Tooltip from './tooltip'
import { currentUserDetails } from '../api/apollo/cache/variables'
import {
  listSavedLinkPerformanceReportsGQL,
  removeSavedLinkPerformanceReport,
  saveLinkPerformanceReport,
  updateLinkPerformanceUserPrefs,
  updateSavedLinkPerformanceReport,
} from '../api/apollo/graphql/report-client'
import { SelectedDimensions } from '../api/types'
import EditIcon from '../assets/edit.svg'
import { availableToShareOptions, messages } from '../core/variables'
import { getItemByKeyValue, hashCode } from '../helpers'
import styles from '../styles/manage-reports-tabs.module.scss'

export const showAccountPermissionLevel = false

interface RenderRowProps {
  children?: React.ReactElement
  item: any
  itemIndex: number
}

export function RenderRow({
  children,
  item,
  itemIndex = 0,
}: RenderRowProps): React.ReactElement {
  return (
    <Row align="space-between" className={styles.row} odd={!(itemIndex % 2)}>
      <div className={styles.tableCell}>
        <span className={styles.tableCellContent}>
          <span className={styles.tableCellContentPadded}>
            {item.reportName}
          </span>
        </span>
      </div>
      <div className={styles.tableCell}>
        <span className={styles.tableCellContent}>
          <span className={styles.tableCellContentPadded}>
            {availableToShareOptions[item.availableTo].long}
            {(showAccountPermissionLevel ||
              (!showAccountPermissionLevel && item.availableTo === 'user')) && (
              <b
                className={classNames(styles.availableToIcon, {
                  [styles[item.availableTo]]: true,
                })}
              />
            )}
          </span>
        </span>
      </div>
      <div className={classNames(styles.tableCell, styles.tableCellButtons)}>
        {children}
      </div>
    </Row>
  )
}

interface ReportDataProps {
  selectedDimensions: SelectedDimensions
  currentDate: string
  startDate: Date
  endDate?: Date
  matchType: MatchTypeProps
  availableTo: any
  breakdownDimension: string
  successMetric: string
  reportName: string
  granularity: Granularity
}

export const getNewReportData = ({
  selectedDimensions,
  currentDate,
  startDate,
  endDate,
  matchType,
  availableTo,
  breakdownDimension,
  successMetric,
  reportName,
  granularity,
}: ReportDataProps) => {
  const filterList = Object.keys(selectedDimensions).map((key) => {
    return {
      dimensionName: key,
      dimensionParameterID: selectedDimensions[key].dimensionParameterID,
      dimensionOptions: selectedDimensions[key].dimensionOptions,
    }
  })

  const newReportData = {
    filterList,
    startDate:
      currentDate === 'custom'
        ? moment(startDate).format('YYYY-MM-DD')
        : currentDate,
    endDate:
      currentDate === 'custom' ? moment(endDate).format('YYYY-MM-DD') : '',
    matchType: matchType.value,
    availableTo,
    stackDimension: breakdownDimension,
    selectedMetric: successMetric,
    reportName,
    reportTitle: reportName,
    granularity,
  }

  return newReportData
}

interface SortableProps {
  children?: React.ReactElement
  itemIndex: number
  item: any
}

const SortableItem = SortableElement(
  ({ children, itemIndex, item }: SortableProps) => {
    return (
      <RenderRow item={item} itemIndex={itemIndex}>
        {children}
      </RenderRow>
    )
  },
)

const SortableWrapper = SortableContainer(
  ({ savedReports, userPreferredOrder, onEdit }) => {
    const [removeSavedReport] = useMutation(removeSavedLinkPerformanceReport, {
      refetchQueries: [listSavedLinkPerformanceReportsGQL],
    })

    if (savedReports.length === 0) {
      return null
    }

    return (
      <div className={styles.tableBody}>
        {userPreferredOrder.map((orderKey, index) => {
          const found = getItemByKeyValue(
            savedReports,
            'savedReportID',
            orderKey,
          )
          if (found !== -1) {
            return (
              // @ts-ignore
              <SortableItem
                // disabled={isMobile}
                index={index}
                key={nanoid()}
                itemIndex={index}
                item={found}
                // setupStatus={setupStatus}
              >
                <>
                  <Button
                    variant="text"
                    className={styles.editButton}
                    icon={{
                      src: EditIcon,
                      alt: 'Edit',
                      iconAfter: true,
                    }}
                    onPress={() => onEdit(orderKey)}
                  >
                    Edit
                  </Button>
                  <DeleteButtonWithConfirmation
                    odd={!(index % 2)}
                    className={styles.deleteButtons}
                    onClick={async () => {
                      await removeSavedReport({
                        variables: { savedReportID: orderKey },
                      })
                    }}
                  >
                    <p>Are you sure to delete this report?</p>
                  </DeleteButtonWithConfirmation>
                </>
              </SortableItem>
            )
          }
          return <span key={nanoid()} />
        })}
      </div>
    )
  },
)

interface ManageReportsModalProps {
  toggleActive: React.Dispatch<React.SetStateAction<boolean>>
  afterClose?: () => void
  onCreateNewReport: () => void
  onSortEnd: (any) => void
  onSortOver: (any) => void
  savedReports: any[]
  userPreferredOrder: string[]
  onEdit: (id: string) => void
}

export function ManageReportsModal({
  toggleActive,
  afterClose,
  onCreateNewReport,
  savedReports,
  userPreferredOrder,
  onSortEnd,
  onSortOver,
  onEdit,
}: ManageReportsModalProps) {
  const showReportsTable = useMemo(() => {
    return savedReports && savedReports.length > 0
  }, [savedReports])

  return (
    <Modal
      className={styles.manageReporstModal}
      setIsOpen={toggleActive}
      afterClose={afterClose}
      width="wide"
      modalHeader="Manage reports"
      yesText="Create a new report"
      onYes={onCreateNewReport}
    >
      {showReportsTable && (
        <div className={styles.wrapper}>
          <div className={styles.body}>
            <div className={styles.reportsList}>
              <div className={styles.table}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableCell}>
                    <span className={styles.tableCellContent}>Report name</span>
                  </div>
                  <div className={styles.tableCell}>
                    <span className={styles.tableCellContent}>
                      Who can see this
                    </span>
                  </div>
                  <div className={styles.tableCell}>
                    <span className={styles.tableCellContent}>Actions</span>
                  </div>
                </div>
                <SortableWrapper
                  // @ts-ignore
                  onEdit={onEdit}
                  onSortEnd={onSortEnd}
                  onSortOver={onSortOver}
                  savedReports={savedReports}
                  userPreferredOrder={userPreferredOrder}
                  helperClass={styles.itemDragged}
                  lockAxis="y"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

interface SaveEditReportModalProps {
  toggleActive: React.Dispatch<React.SetStateAction<boolean>>
  onSuccess: (val: any) => void
  onCancel?: () => void
  item?: any
  defaultReportName?: string
}

export function SaveEditReportModal({
  toggleActive,
  onSuccess,
  onCancel,
  item = null,
  defaultReportName = '',
}: SaveEditReportModalProps): React.ReactElement {
  const { reportName: _reportName, availableTo: _availableTo } = item || {
    reportName: defaultReportName,
    availableTo: 'user',
  }

  const [reportName, setReportName] = useState(_reportName)
  const [availableTo, setAvailableTo] = useState(_availableTo)

  useEffect(() => {
    setReportName(_reportName)
  }, [defaultReportName])

  return (
    <Modal
      setIsOpen={toggleActive}
      afterClose={() => {
        if (onCancel) {
          onCancel()
        }
      }}
      modalHeader={item ? 'Update existing report' : 'Create a new report'}
      yesText={item ? 'Update' : 'Create'}
      onYes={() => onSuccess({ reportName, availableTo })}
    >
      <LabelSlot topAlign className={styles.fullWidth}>
        <Label modalHeading>
          <Tooltip
            id="report-name-tooltip"
            useIcon
            tooltipMessage="Name of report to display in tab."
          >
            Report name
          </Tooltip>
        </Label>
      </LabelSlot>
      <Input
        name="reportName"
        id="reportName"
        type="text"
        value={reportName}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          const { value: val } = event.target
          setReportName(val)
        }}
      />
      <LabelSlot topAlign className={styles.fullWidth}>
        <Label modalHeading>Who can see this</Label>
      </LabelSlot>
      <div className={styles.radioPanel}>
        <Input
          type="radio"
          id="availableTo-1"
          name="availableTo"
          className={classNames(styles.availableToInput, {
            [styles.availableToInputCompany]:
              true && showAccountPermissionLevel,
          })}
          label={availableToShareOptions.company.long}
          checked={availableTo === 'company'}
          onClick={() => setAvailableTo('company')}
        >
          <p>{availableToShareOptions.company.desc}</p>
        </Input>
        {showAccountPermissionLevel && (
          <Input
            type="radio"
            id="availableTo-3"
            name="availableTo"
            className={classNames(styles.availableToInput, {
              [styles.availableToInputAccount]:
                true && showAccountPermissionLevel,
            })}
            label={availableToShareOptions.account.long}
            checked={availableTo === 'account'}
            onClick={() => setAvailableTo('account')}
          >
            <p>{availableToShareOptions.account.desc}</p>
          </Input>
        )}
        <Input
          type="radio"
          id="availableTo-2"
          name="availableTo"
          className={classNames(styles.availableToInput, {
            [styles.availableToInputUser]: true,
          })}
          label={availableToShareOptions.user.long}
          checked={availableTo === 'user'}
          onClick={() => setAvailableTo('user')}
        >
          <p>{availableToShareOptions.user.desc}</p>
        </Input>
      </div>
    </Modal>
  )
}

interface ManageReportsProps {
  selectedDimensions: any
  startDate: Date
  endDate: Date
  matchType: any
  breakdownDimension: string
  successMetric: any
  currentDate: string
  granularity: Granularity
  showEdit?: boolean
  showSave?: boolean
  setManageReportsModalType?: React.Dispatch<
    React.SetStateAction<'manage' | 'save' | null>
  >
}

export function ManageReports({
  selectedDimensions,
  startDate,
  endDate,
  matchType,
  breakdownDimension,
  successMetric,
  currentDate,
  granularity,
  showEdit = false,
  showSave = false,
  setManageReportsModalType,
}: ManageReportsProps): React.ReactElement {
  const { workspaceID } = useReactiveVar(currentUserDetails)

  const [getSavedReports, { data: savedReportsData }] = useLazyQuery(
    listSavedLinkPerformanceReportsGQL,
  )

  const [saveReport] = useMutation(saveLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateSavedReport] = useMutation(updateSavedLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateReportPrefs] = useMutation(updateLinkPerformanceUserPrefs, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })

  const [showManageModal, setShowManageModal] = useState(showEdit)
  const [showCreateModal, setShowCreateModal] = useState(showSave)
  const [currentReport, setCurrentReport] = useState<any>(null)
  const [currentOrder, setCurrentOrder] = useState<string[]>([])

  useEffect(() => {
    if (!workspaceID) return

    getSavedReports()
  }, [workspaceID])

  useEffect(() => {
    setShowManageModal(showEdit)
  }, [showEdit])

  useEffect(() => {
    setShowCreateModal(showSave)
  }, [showSave])

  const listSavedLinkPerformanceReports = useMemo(() => {
    if (!savedReportsData) return null

    // HACK: remove empty array value from applyFilters
    // this is a compatibility hack where this was causing issues on download to Excel
    return {
      ...savedReportsData.report.listSavedLinkPerformanceReports,
      savedReports: savedReportsData.report.listSavedLinkPerformanceReports.savedReports.map(
        (report) => {
          return {
            ...report,
            applyFilters:
              report.applyFilters?.filter(
                (aF) => aF.dimensionOptions.length > 0,
              ) || [],
          }
        },
      ),
    }
  }, [savedReportsData])

  const {
    savedReports,
    userPreferredOrder,
  } = listSavedLinkPerformanceReports || {
    savedReports: [],
    userPreferredOrder: [],
  }

  useEffect(() => {
    setCurrentOrder(userPreferredOrder)
  }, [userPreferredOrder])

  const onSortEnd = useCallback(() => {
    updateReportPrefs({
      variables: {
        newOrder: currentOrder,
      },
    })
  }, [currentOrder])

  const onSortOver = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const orderedArray = arrayMove(userPreferredOrder, oldIndex, newIndex)

      setCurrentOrder(orderedArray)
    }
  }

  const onEdit = (savedReportID) => {
    const found = getItemByKeyValue(
      savedReports,
      'savedReportID',
      savedReportID,
    )
    if (found !== -1) {
      setShowCreateModal(true)
      setCurrentReport(found)
    }
  }

  return (
    <>
      {showCreateModal && (
        <SaveEditReportModal
          defaultReportName={messages.defaultReportTitle}
          onCancel={() => {
            setShowManageModal(true)
          }}
          item={currentReport}
          toggleActive={setShowCreateModal}
          onSuccess={({ reportName, availableTo }) => {
            if (currentReport === null) {
              const newReportData = getNewReportData({
                selectedDimensions,
                currentDate,
                startDate,
                endDate,
                matchType,
                availableTo,
                breakdownDimension,
                successMetric,
                reportName,
                granularity,
              })
              saveReport({ variables: newReportData })
            } else {
              updateSavedReport({
                variables: {
                  ...currentReport,
                  availableTo,
                  reportName,
                  reportTitle: reportName,
                },
              })
            }
            setShowCreateModal(false)
          }}
        />
      )}
      {showManageModal && (
        <ManageReportsModal
          onEdit={onEdit}
          onSortEnd={onSortEnd}
          onSortOver={onSortOver}
          savedReports={savedReports}
          userPreferredOrder={userPreferredOrder}
          onCreateNewReport={() => {
            setShowManageModal(false)
            setShowCreateModal(true)
          }}
          toggleActive={setShowManageModal}
          afterClose={() => {
            if (setManageReportsModalType) {
              setManageReportsModalType(null)
            }
          }}
        />
      )}
    </>
  )
}

interface ManageReportsTabsSaveButtonProps {
  selectedDimensions: SelectedDimensions
  startDate: Date
  endDate: Date
  matchType: any
  breakdownDimension: string
  successMetric: any
  currentReport: any
  onSaveReport: () => void
  disabled: boolean
  currentDate: string
  granularity: Granularity
  defaultReportName?: string
}

export function ManageReportsTabsSaveButton({
  selectedDimensions,
  startDate,
  endDate,
  matchType,
  breakdownDimension,
  successMetric,
  currentReport,
  onSaveReport,
  disabled,
  currentDate,
  granularity,
  defaultReportName,
}: ManageReportsTabsSaveButtonProps) {
  const [saveReport] = useMutation(saveLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateSavedReport] = useMutation(updateSavedLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })

  const [showCreateModal, setShowCreateModal] = useState(false)

  if (disabled || !successMetric) return null

  return (
    <>
      <Button
        variant="secondary"
        color="blue"
        style={{ padding: '2px 12px' }}
        onPress={() => {
          const filterList = Object.keys(selectedDimensions).map((key) => {
            return {
              dimensionParameterID:
                selectedDimensions[key].dimensionParameterID,
              dimensionName: key,
              dimensionOptions: selectedDimensions[key].dimensionOptions,
            }
          })

          if (currentReport) {
            onSaveReport()

            updateSavedReport({
              variables: {
                ...currentReport,
                filterList,
                startDate:
                  currentDate === 'custom'
                    ? moment(startDate).format('YYYY-MM-DD')
                    : currentDate,
                endDate:
                  currentDate === 'custom'
                    ? moment(endDate).format('YYYY-MM-DD')
                    : '',
                matchType: Object.prototype.hasOwnProperty.call(matchType, 'id')
                  ? matchType.id
                  : matchType.value,
                stackDimension: breakdownDimension,
                selectedMetric: successMetric,
                granularity,
              },
            })
          } else {
            setShowCreateModal(true)
          }
        }}
      >
        {currentReport === null ? 'Save new report' : 'Save changes'}
      </Button>
      {showCreateModal && (
        <SaveEditReportModal
          onCancel={() => setShowCreateModal(false)}
          defaultReportName={defaultReportName}
          item={currentReport}
          toggleActive={setShowCreateModal}
          onSuccess={({ reportName, availableTo }) => {
            if (!currentReport) {
              const newReportData = getNewReportData({
                selectedDimensions,
                currentDate,
                startDate,
                endDate,
                matchType,
                availableTo,
                breakdownDimension,
                successMetric,
                reportName,
                granularity,
              })

              saveReport({ variables: newReportData })
            } else {
              updateSavedReport({
                variables: {
                  ...currentReport,
                  availableTo,
                  reportName,
                },
              })
            }
            setShowCreateModal(false)
          }}
        />
      )}
    </>
  )
}

interface ManageReportsTabsProps {
  selectedDimensions: any
  startDate: Date
  endDate: Date
  matchType: any
  breakdownDimension: string
  successMetric: any
  onChangeReport: (report: SavedReport) => void
  onResetReport: () => void
  onSaveReport: () => void
  showSaveButton: boolean
  actionButtons: React.ReactNode
  currentDate: string
  granularity: Granularity
  loading?: boolean
  defaultReportName?: string
}

export default function ManageReportsTabs({
  selectedDimensions,
  startDate,
  endDate,
  matchType,
  breakdownDimension,
  successMetric,
  onChangeReport,
  onResetReport,
  onSaveReport,
  showSaveButton,
  actionButtons,
  currentDate,
  granularity,
  loading,
  defaultReportName = '',
}: ManageReportsTabsProps) {
  const { workspaceID } = useReactiveVar(currentUserDetails)

  const [
    getSavedReports,
    { data: savedReportsData, loading: loadingSavedReports },
  ] = useLazyQuery(listSavedLinkPerformanceReportsGQL)

  const [saveReport] = useMutation(saveLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateSavedReport] = useMutation(updateSavedLinkPerformanceReport, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })
  const [updateReportPrefs] = useMutation(updateLinkPerformanceUserPrefs, {
    refetchQueries: [listSavedLinkPerformanceReportsGQL],
  })

  useEffect(() => {
    if (!workspaceID) return

    getSavedReports()
  }, [workspaceID])

  const listSavedLinkPerformanceReports = useMemo(() => {
    if (!savedReportsData) return null

    // HACK: remove empty array value from applyFilters
    // this is a compatibility hack where this was causing issues on download to Excel
    return {
      ...savedReportsData.report.listSavedLinkPerformanceReports,
      savedReports: savedReportsData.report.listSavedLinkPerformanceReports.savedReports.map(
        (report) => {
          return {
            ...report,
            applyFilters:
              report.applyFilters?.filter(
                (aF) => aF.dimensionOptions.length > 0,
              ) || [],
          }
        },
      ),
    }
  }, [savedReportsData])

  const [currentIndex, setCurrentIndex] = useState(0)
  const [showCreateEditModal, setShowCreateEditModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [currentReport, setCurrentReport] = useState<any>(null)
  const [currentOrder, setCurrentOrder] = useState<string[]>([])

  useEffect(() => {
    onChangeReport(currentReport)
  }, [currentReport])

  const {
    savedReports,
    userPreferredOrder,
  } = listSavedLinkPerformanceReports || {
    savedReports: [],
    userPreferredOrder: [],
  }

  useEffect(() => {
    if (currentReport !== null) {
      const { savedReportID } = currentReport
      const found = getItemByKeyValue(
        savedReports,
        'savedReportID',
        savedReportID,
      )
      if (found !== -1) {
        const currentHash = hashCode(JSON.stringify(currentReport))
        const foundHash = hashCode(JSON.stringify(found))
        if (currentHash !== foundHash) {
          // Update report if/when there is a change in data
          onChangeReport(found)
        }
      }
    }
  }, [savedReports])

  useEffect(() => {
    setCurrentOrder(userPreferredOrder)
  }, [userPreferredOrder])

  const onSortEnd = useCallback(() => {
    updateReportPrefs({
      variables: {
        newOrder: currentOrder,
      },
    })
  }, [currentOrder])

  const onSortOver = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const orderedArray = arrayMove(userPreferredOrder, oldIndex, newIndex)

      setCurrentOrder(orderedArray)
    }
  }

  const onEdit = (savedReportID) => {
    const found = getItemByKeyValue(
      savedReports,
      'savedReportID',
      savedReportID,
    )
    if (found !== -1) {
      setCurrentReport(found)
      setShowCreateEditModal(true)
    }
  }
  const tabsLabels = useMemo(() => {
    const res: (string | React.ReactElement)[] = ['Unfiltered report']
    if (userPreferredOrder.length > 2) {
      userPreferredOrder.forEach((orderKey) => {
        const found = getItemByKeyValue(savedReports, 'savedReportID', orderKey)
        if (found !== -1) {
          const { reportName, availableTo } = found
          if (reportName) {
            res.push(
              `${reportName} ${availableTo === 'user' ? `(private)` : ''}`,
            )
          }
        }
      })
      return res
    }
    userPreferredOrder.forEach((orderKey) => {
      const found = getItemByKeyValue(savedReports, 'savedReportID', orderKey)
      if (found !== -1) {
        const { reportName, availableTo } = found
        if (reportName) {
          res.push(
            <em key={nanoid()} className={styles.tabText}>
              {reportName}{' '}
              {(showAccountPermissionLevel ||
                (!showAccountPermissionLevel && availableTo === 'user')) && (
                <b
                  className={classNames(styles.availableToIcon, {
                    [styles[availableTo]]: true,
                  })}
                />
              )}
            </em>,
          )
        }
      }
    })

    return res
  }, [userPreferredOrder])

  return (
    <>
      <HeaderPanel className={styles.headerPanel}>
        <Panel className={styles.panel}>
          <ButtonTabs
            className={styles.buttonTabs}
            showOrphansAsTab
            selected={currentIndex}
            isTopOfBox
            showDropDown
            tabsLabels={tabsLabels}
            type="tabs"
            onChange={(index: number) => {
              if (index === currentIndex) {
                onResetReport()
              } else if (index === 0) {
                setCurrentReport(null)
              } else {
                const found = { ...savedReports[index - 1] }
                setCurrentReport(found)
              }
              setCurrentIndex(index)
            }}
          >
            {[
              ...tabsLabels.map((item) => {
                return (
                  <span key={nanoid()} className={styles.tabBoxPlaceholder}>
                    {item}
                  </span>
                )
              }),
              loadingSavedReports || loading ? (
                <div key={nanoid()} className={styles.afterTabsButtons} />
              ) : (
                <div
                  key="modal-edit-button"
                  className={styles.afterTabsButtons}
                >
                  <ManageReportsTabsSaveButton
                    defaultReportName={defaultReportName}
                    granularity={granularity}
                    currentDate={currentDate}
                    disabled={!showSaveButton}
                    currentReport={currentReport}
                    selectedDimensions={selectedDimensions}
                    startDate={startDate}
                    endDate={endDate}
                    matchType={matchType}
                    breakdownDimension={breakdownDimension}
                    successMetric={successMetric}
                    onSaveReport={onSaveReport}
                  />
                </div>
              ),
              <div key={nanoid()} className={styles.afterTabsActionButtons}>
                {actionButtons}
              </div>,
            ]}
          </ButtonTabs>
        </Panel>
      </HeaderPanel>
      {showCreateEditModal && (
        <SaveEditReportModal
          defaultReportName={defaultReportName}
          onCancel={() => {
            setCurrentReport(null)
            setShowEditModal(true)
          }}
          item={currentReport}
          toggleActive={setShowCreateEditModal}
          onSuccess={({ reportName, availableTo }) => {
            if (!currentReport) {
              const newReportData = getNewReportData({
                selectedDimensions,
                currentDate,
                startDate,
                endDate,
                matchType,
                availableTo,
                breakdownDimension,
                successMetric,
                reportName,
                granularity,
              })
              saveReport({ variables: newReportData })
            } else {
              updateSavedReport({
                variables: {
                  ...currentReport,
                  availableTo,
                  reportTitle: reportName,
                },
              })
            }
            setShowCreateEditModal(false)
          }}
        />
      )}
      {showEditModal && (
        <ManageReportsModal
          onEdit={onEdit}
          onSortEnd={onSortEnd}
          onSortOver={onSortOver}
          savedReports={savedReports}
          userPreferredOrder={userPreferredOrder}
          onCreateNewReport={() => {
            setCurrentReport(null)
            setShowEditModal(false)
            setShowCreateEditModal(true)
          }}
          toggleActive={setShowEditModal}
        />
      )}
    </>
  )
}
